import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { mergeShared, resetSharedCache } from "@/lib/useSharedSections";
import { NO_FEE_DEFAULT, SHARED_DEFAULTS } from "@/lib/sharedSectionDefaults";
import { HOW_IT_WORKS } from "@/pages/landing/caseFunnel/caseContent";
import { AdminModal, BTN, BTN_BLACK, BTN_WHITE, INPUT, errText } from "./AdminModal";
import { ListEditor } from "./ListEditor";

const BADGE_FIELDS = [{ k: "title", placeholder: "Title", max: 40 }, { k: "sub", placeholder: "Small print", max: 80 }];
const RESULT_FIELDS = [{ k: "amount", placeholder: "$1.2M", max: 20 }, { k: "label", placeholder: "Truck accident settlement", max: 100 }];
const STEP_FIELDS = [{ k: "title", placeholder: "Step title", max: 60 }, { k: "body", placeholder: "Short description", max: 240 }];
const FAQ_FIELDS = [{ k: "q", placeholder: "Question", max: 160, wide: true }, { k: "a", placeholder: "Answer", max: 800, area: true, wide: true }];
const ARTICLE_FIELDS = [
  { k: "title", placeholder: "Article title", max: 120 }, { k: "url", placeholder: "Link (https://…)", max: 500 },
  { k: "image_url", placeholder: "Image link (optional) — or upload one", max: 500, wide: true, upload: true },
];

function toForm(saved) {
  const m = mergeShared(saved);
  return {
    trust_badges: m.trust_badges, case_results: m.case_results, results_disclaimer: m.results_disclaimer,
    custom_how: Boolean(saved.how_it_works), how_it_works: saved.how_it_works || HOW_IT_WORKS.map(({ title, body }) => ({ title, body })),
    custom_faq: Boolean(saved.faq), faq: saved.faq || [],
    custom_cta: Boolean(saved.final_cta), final_cta: saved.final_cta || { title: "", body: "", button: "" },
    no_fee_title: m.no_fee.title, no_fee_items: m.no_fee.items.map((t) => ({ t })),
    articles: m.articles, footer_note: m.footer_note || "",
  };
}

const opt = (v) => (v || "").trim() || null;
// Untouched sections are sent as null so future built-in defaults still reach every page.
const unlessDefault = (v, k) => (JSON.stringify(v) === JSON.stringify(SHARED_DEFAULTS[k]) ? null : v);
function toPayload(f) {
  return {
    trust_badges: unlessDefault(f.trust_badges.filter((b) => b.title.trim()), "trust_badges"),
    case_results: unlessDefault(f.case_results.filter((r) => r.amount.trim()), "case_results"),
    results_disclaimer: unlessDefault(f.results_disclaimer, "results_disclaimer"),
    how_it_works: f.custom_how ? f.how_it_works.filter((s) => s.title.trim()) : null,
    faq: f.custom_faq ? f.faq.filter((x) => x.q.trim() && x.a.trim()) : null,
    final_cta: f.custom_cta && f.final_cta.title.trim() ? f.final_cta : null,
    no_fee: unlessDefault({ title: f.no_fee_title.trim() || NO_FEE_DEFAULT.title, items: f.no_fee_items.map((x) => x.t.trim()).filter(Boolean) }, "no_fee"),
    articles: f.articles.filter((a) => a.title.trim() && a.url.trim()).map((a) => ({ ...a, image_url: opt(a.image_url) })),
    footer_note: f.footer_note,
  };
}

const Sec = ({ title, hint, children, testId, toggle }) => (
  <section className="mb-5 border-b border-zinc-200 pb-4" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="font-chivo font-black">{title}</div>
      {toggle && <div className="ml-auto">{toggle}</div>}
    </div>
    {hint && <p className="mb-2 mt-0.5 text-[11px] text-zinc-600">{hint}</p>}
    {children}
  </section>
);

const OwnToggle = ({ on, set, testId }) => (
  <label className="flex items-center gap-2 text-[11px]">
    Custom <Switch checked={on} onCheckedChange={set} data-testid={testId} />
  </label>
);

function LandingTab({ f, set }) {
  return (
    <>
      <Sec title="Trust badges" hint="The strip of reasons to trust you under the top of each page." testId="shared-sec-badges">
        <ListEditor items={f.trust_badges} onChange={(v) => set("trust_badges", v)} fields={BADGE_FIELDS} max={6} testId="shared-badges" addLabel="Add badge" />
      </Sec>
      <Sec title="Case results" testId="shared-sec-results">
        <ListEditor items={f.case_results} onChange={(v) => set("case_results", v)} fields={RESULT_FIELDS} max={9} testId="shared-results" addLabel="Add result" />
        <label className="label-mono mt-3 block">Results disclaimer
          <textarea className={`${INPUT} h-16 resize-none`} maxLength={400} value={f.results_disclaimer}
                    onChange={(e) => set("results_disclaimer", e.target.value)} data-testid="shared-results-disclaimer" />
        </label>
      </Sec>
      <Sec title="How it works" hint={f.custom_how ? "Used on every page." : "Each page keeps its own steps."} testId="shared-sec-how"
           toggle={<OwnToggle on={f.custom_how} set={(v) => set("custom_how", v)} testId="shared-how-custom" />}>
        {f.custom_how && <ListEditor items={f.how_it_works} onChange={(v) => set("how_it_works", v)} fields={STEP_FIELDS} max={4} testId="shared-how" addLabel="Add step" />}
      </Sec>
      <Sec title="Questions (FAQ)" hint={f.custom_faq ? "Replaces each case type's own questions." : "Each case type keeps its own questions."} testId="shared-sec-faq"
           toggle={<OwnToggle on={f.custom_faq} set={(v) => set("custom_faq", v)} testId="shared-faq-custom" />}>
        {f.custom_faq && <ListEditor items={f.faq} onChange={(v) => set("faq", v)} fields={FAQ_FIELDS} max={10} testId="shared-faq" addLabel="Add question" />}
      </Sec>
      <Sec title="Closing call-to-action" hint={f.custom_cta ? "Use {firm} to insert the firm's name." : "Each page keeps its own closing wording."} testId="shared-sec-cta"
           toggle={<OwnToggle on={f.custom_cta} set={(v) => set("custom_cta", v)} testId="shared-cta-custom" />}>
        {f.custom_cta && (
          <div className="grid gap-x-2 sm:grid-cols-2">
            {[["title", "Headline", 160], ["body", "Line under it", 240], ["button", "Button text", 40]].map(([k, l, max]) => (
              <label key={k} className={`label-mono mt-1 block ${k === "title" ? "sm:col-span-2" : ""}`}>{l}
                <input className={INPUT} maxLength={max} value={f.final_cta[k] || ""}
                       onChange={(e) => set("final_cta", { ...f.final_cta, [k]: e.target.value })} data-testid={`shared-cta-${k}`} />
              </label>
            ))}
          </div>
        )}
      </Sec>
      <Sec title="Footer note" hint="Optional line shown above the legal footer on every page." testId="shared-sec-footer">
        <textarea className={`${INPUT} h-14 resize-none`} maxLength={400} value={f.footer_note}
                  onChange={(e) => set("footer_note", e.target.value)} data-testid="shared-footer-note" />
      </Sec>
    </>
  );
}

function ThankYouTab({ f, set }) {
  return (
    <>
      <Sec title="&ldquo;No fee unless we win&rdquo;" hint="Used by thank-you designs with this section turned on." testId="shared-sec-no-fee">
        <label className="label-mono block">Headline
          <input className={INPUT} maxLength={80} value={f.no_fee_title} onChange={(e) => set("no_fee_title", e.target.value)} data-testid="shared-no-fee-title" />
        </label>
        <div className="mt-2">
          <ListEditor items={f.no_fee_items} onChange={(v) => set("no_fee_items", v)} fields={[{ k: "t", placeholder: "e.g. No upfront costs", max: 60 }]} max={6} testId="shared-no-fee-items" addLabel="Add point" />
        </div>
      </Sec>
      <Sec title="Helpful articles" hint="Reading for after an injury. Hidden on thank-you pages until you add at least one." testId="shared-sec-articles">
        <ListEditor items={f.articles} onChange={(v) => set("articles", v)} fields={ARTICLE_FIELDS} max={6} testId="shared-articles" addLabel="Add article" />
      </Sec>
    </>
  );
}

export function SharedSectionsDialog({ onClose }) {
  const [f, setF] = useState(null);
  const [tab, setTab] = useState("landing");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/shared-sections").then((r) => setF(toForm(r.data || {})))
      .catch((e) => { toast.error(errText(e, "Couldn't load shared sections")); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shared-sections", toPayload(f));
      resetSharedCache();
      toast.success("Shared sections saved — live on every page");
      onClose();
    } catch (e) {
      toast.error(errText(e, "Couldn't save shared sections"));
    } finally {
      setSaving(false);
    }
  };
  const footer = (
    <>
      <button type="button" className={BTN_WHITE} onClick={onClose} data-testid="shared-cancel">Cancel</button>
      <button type="button" className={BTN_BLACK} onClick={save} disabled={!f || saving} data-testid="shared-save">
        {saving ? "Saving…" : "Save shared sections"}
      </button>
    </>
  );
  return (
    <AdminModal eyebrow="Shared sections" title="Content used on every page" onClose={onClose} footer={footer} testId="shared-sections-modal">
      <div className="mb-4 flex border-2 border-black" data-testid="shared-tabs">
        {[["landing", "Landing pages"], ["thankyou", "Thank-you pages"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`shared-tab-${k}`}
                  className={`${BTN} flex-1 justify-center border-0 border-r-2 last:border-r-0 ${tab === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
            {l}
          </button>
        ))}
      </div>
      {!f ? <div className="label-mono py-4">Loading…</div> : tab === "landing" ? <LandingTab f={f} set={set} /> : <ThankYouTab f={f} set={set} />}
    </AdminModal>
  );
}
